
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from "rxjs";
import { filter, map, takeUntil } from 'rxjs/operators';
import { UserService } from './user.service';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';


@Injectable()
export class AuthService {
	private readonly _destroying$ = new Subject<void>();
	private interactionInProgress = false;
	private userId: string = '0'; // Default user ID
	private sessionId: string = '0'; // Default session ID
	private currentMsalInteractionStatus: InteractionStatus = InteractionStatus.None;
	private ssoRegistrationData: any = null; // Store SSO data for first-time registration

	constructor(
		private httpClient: HttpClient,
		private userService: UserService,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService
	) {
		// Initialize the user ID
		this.initializeUserId();

		// Check and reset stale interaction status on initialization
		this.checkAndResetInteractionStatus();

		// Initialize the interaction status based on the current value
		// This is important for page refreshes
		this.msalBroadcastService.inProgress$
			.pipe(
				takeUntil(this._destroying$)
			)
			.subscribe((status: InteractionStatus) => {
				// Track the current MSAL interaction status
				this.currentMsalInteractionStatus = status;

				// Only set interactionInProgress to true if the status is from a login attempt
				// This ensures that the "SSO Login in progress..." message only appears after a login has been attempted
				if (status === InteractionStatus.Login) {
					this.interactionInProgress = true;
					console.log('Setting interactionInProgress to true because a login attempt is in progress');
				} else if (status === InteractionStatus.None) {
					this.interactionInProgress = false;
					console.log('Setting interactionInProgress to false because no interaction is in progress');
				} else {
					// For other interaction types, don't change the interactionInProgress property
					// This ensures that we don't show the "SSO Login in progress..." message for non-login interactions
					console.log('Not changing interactionInProgress because the interaction is not a login attempt');
				}

				// Store the interaction status in Redis if it changes
				if (status !== InteractionStatus.None) {
					this.storeInteractionStatus(status);
				}

				// Log the current interaction status for debugging
				console.log('MSAL interaction status:', {
					status,
					isInProgress: this.interactionInProgress
				});
			});
	}

	/**
	 * Initializes the user ID from the UserService
	 * This is used to create Redis keys for storing interaction status
	 */
	private initializeUserId(): void {
		const currentUser = this.userService.getCurrentUser();
		if (currentUser && currentUser.id) {
			this.userId = currentUser.id.toString();
		} else {
			// If there's no current user, use session ID as fallback
			// This ensures we have a unique identifier even before login
			this.userId = sessionStorage.getItem('sessionId') || Date.now().toString();
		}

		console.log('Initialized user ID for MSAL interaction status:', this.userId);

		// Initialize session ID (could be from session storage or generated)
		this.sessionId = sessionStorage.getItem('sessionId') || Date.now().toString();
		if (!sessionStorage.getItem('sessionId')) {
			sessionStorage.setItem('sessionId', this.sessionId);
		}
		console.log('Initialized session ID:', this.sessionId);
	}

	/**
	 * Updates the user ID after successful login
	 * This should be called after the user is authenticated and userService has the user info
	 */
	public updateUserIdAfterLogin(): void {
		const currentUser = this.userService.getCurrentUser();
		if (currentUser && currentUser.id) {
			const newUserId = currentUser.id.toString();
			if (newUserId !== this.userId) {
				console.log('Updating user ID from', this.userId, 'to', newUserId);
				this.userId = newUserId;
			}
		}
	}

	/**
	 * Stores the interaction status in Redis
	 * @param status The interaction status to store
	 * @param timeoutMinutes The number of minutes after which the interaction status should be automatically cleared (default: 2)
	 * ENHANCED: Reduced timeout from 5 minutes to 2 minutes for faster cleanup
	 */
	private storeInteractionStatus(status: InteractionStatus, timeoutMinutes: number = 2): void {
		// Calculate the expiration time in milliseconds
		const expirationTime = Date.now() + (timeoutMinutes * 60 * 1000);

		// Store the interaction status in Redis
		this.httpClient.post('/msalAuth/interactionStatus/set', {
			status: status.toString(),
			timestamp: Date.now(),
			userId: this.userId,
			expirationTime: expirationTime,
			requestData: null // We don't need to store the request data for now
		}).subscribe(
			(response: any) => {
				console.log('Stored interaction status in Redis:', response);
			},
			(error) => {
				console.error('Failed to store interaction status in Redis:', error);

				// Fall back to localStorage if Redis storage fails
				localStorage.setItem('msal.interaction.status', status.toString());
				localStorage.setItem('msal.interaction.timestamp', Date.now().toString());
				localStorage.setItem('msal.interaction.expiration', expirationTime.toString());
			}
		);

		// Set a timeout to automatically clear the interaction status after the specified time
		setTimeout(() => {
			// Check if the interaction is still in progress
			if (this.interactionInProgress) {
				console.log(`Automatically clearing MSAL interaction status after ${timeoutMinutes} minutes`);
				this.clearInteractionStatus();
			}
		}, timeoutMinutes * 60 * 1000);
	}

 /**
	 * Checks if there's an active interaction in Redis, and if so, clears it if it's stale
	 * This is important for page refreshes
	 * ENHANCED: More aggressive cleanup to prevent stuck authentication states
	 */
	private checkAndResetInteractionStatus(): void {
		// Check if there's an interaction status in Redis
		this.httpClient.get(`/msalAuth/interactionStatus/get/${this.userId}`)
			.subscribe(
				(response: any) => {
					// Log the current interaction status and timestamp for debugging
					console.log('MSAL interaction status check from Redis:', response);

					// If the interaction status is stale, it will already be cleared by the server
					// If it's not stale and not 'none', check if we should clear it proactively
					if (response && response.status && response.status.toLowerCase() !== 'none' && !response.stale) {
						const now = Date.now();
						const timestamp = response.timestamp;
						const expirationTime = response.expirationTime;

						// ENHANCED: More aggressive stale detection - clear if older than 1 minute on startup
						const ageInMinutes = timestamp ? (now - timestamp) / (1000 * 60) : 0;
						const isOldInteraction = ageInMinutes > 1; // Reduced from 5 minutes to 1 minute

						if (expirationTime && now > expirationTime) {
							// The interaction has expired, clear it

							this.forceClearAllInteractionState();
							this.interactionInProgress = false;
						} else if (isOldInteraction) {
							// ENHANCED: Proactively clear old interactions to prevent stuck states

							this.forceClearAllInteractionState();
							this.interactionInProgress = false;
						} else {
							// Calculate the remaining time until expiration
							const remainingTime = expirationTime ? Math.round((expirationTime - now) / 1000 / 60) : 2;
							console.log(`MSAL interaction in progress, will expire in approximately ${remainingTime} minutes`);

							// Only set interactionInProgress to true if the status is from a login attempt
							// This ensures that the "SSO Login in progress..." message only appears after a login has been attempted
							if (response.status.toLowerCase() === 'login') {
								this.interactionInProgress = true;
								console.log('Setting interactionInProgress to true because a login attempt has been made');
							} else {
								this.interactionInProgress = false;
								console.log('Not setting interactionInProgress to true because no login attempt has been made');
							}

							// Set a timeout to automatically clear the interaction status when it expires
							if (expirationTime) {
								const timeoutMs = Math.max(0, expirationTime - now);
								setTimeout(() => {
									// Check if the interaction is still in progress
									if (this.interactionInProgress) {
										console.log('Automatically clearing expired MSAL interaction status');
										this.forceClearAllInteractionState();
									}
								}, timeoutMs);
							}
						}
					} else {
						this.interactionInProgress = false;
					}
				},
				(error) => {
					console.error('Failed to check interaction status from Redis:', error);

					// Fall back to localStorage if Redis check fails
					this.checkAndResetInteractionStatusFromLocalStorage();
				}
			);
	}

	/**
	 * Fallback method to check interaction status from localStorage
	 * This is used if the Redis check fails
	 * ENHANCED: More aggressive cleanup to prevent stuck authentication states
	 */
	private checkAndResetInteractionStatusFromLocalStorage(): void {
		// Check if there's an interaction status in localStorage
		const inProgress = localStorage.getItem('msal.interaction.status');

		// If there's an interaction status and it's not 'none', check if it's stale
		if (inProgress && inProgress.toLowerCase() !== 'none') {
			// Check if there's a timestamp and expiration time for the interaction
			const timestamp = localStorage.getItem('msal.interaction.timestamp');
			const expirationTime = localStorage.getItem('msal.interaction.expiration');
			const now = Date.now();

			// Log the current interaction status, timestamp, and expiration time for debugging
			console.log('MSAL interaction status check from localStorage:', {
				inProgress,
				timestamp,
				expirationTime,
				now
			});

			// ENHANCED: More aggressive stale detection
			const ageInMinutes = timestamp ? (now - parseInt(timestamp, 10)) / (1000 * 60) : 0;
			const isOldInteraction = ageInMinutes > 1; // Reduced from 5 minutes to 1 minute

			// Check if the interaction has expired
			if (expirationTime && now > parseInt(expirationTime, 10)) {

				this.forceClearAllInteractionState();
			}
			// ENHANCED: If there's no timestamp or expiration time, or if the timestamp is more than 1 minute old, clear the interaction status
			else if (!timestamp || isOldInteraction) {

				this.forceClearAllInteractionState();
			} else {
				// If the interaction status is not stale, check if it's from a login attempt
				// Only set interactionInProgress to true if the status is from a login attempt
				// This ensures that the "SSO Login in progress..." message only appears after a login has been attempted
				if (inProgress.toLowerCase() === 'login') {
					this.interactionInProgress = true;
					console.log('Setting interactionInProgress to true because a login attempt has been made (localStorage)');
				} else {
					this.interactionInProgress = false;
					console.log('Not setting interactionInProgress to true because no login attempt has been made (localStorage)');
				}

				// Calculate the remaining time until expiration
				const remainingTime = expirationTime ? Math.round((parseInt(expirationTime, 10) - now) / 1000 / 60) : 2;
				console.log(`MSAL interaction in progress (localStorage), will expire in approximately ${remainingTime} minutes`);

				// Set a timeout to automatically clear the interaction status when it expires
				if (expirationTime) {
					const timeoutMs = Math.max(0, parseInt(expirationTime, 10) - now);
					setTimeout(() => {
						// Check if the interaction is still in progress
						if (this.interactionInProgress) {
							console.log('Automatically clearing expired MSAL interaction status from localStorage');
							this.forceClearAllInteractionState();
						}
					}, timeoutMs);
				}
			}
		} else {
			this.interactionInProgress = false;
		}
	}

	isAuthenticated(routeSnapshot) {
		let stateName = routeSnapshot?.data?.['stateName'];
		return this.httpClient.post('/auth/isLogged', {stateName: stateName}, {
			observe: "body",
			responseType:"json"
		}).pipe(
		map(
    	data => {

				if (data && data['status'] && data['status'] == 'loggedOut') {
					return false;
				}

				if (data['userInfo'] && data['userAccess']) {
					if (this.userService.getCurrentUser().id != data['userInfo'].id) {
						this.userService.setCurrentUser(data['userInfo'], data['userAccess']);
					}
					return true;
				}
    	}
  	));
	}

	/**
	 * Check if user has a valid session without requiring route data
	 * Returns the full session response for use in components
	 */
	checkSession() {
		return this.httpClient.post('/auth/isLogged', {stateName: null}, {
			observe: "body",
			responseType:"json"
		});
	}

	registerUser(user) {
		return this.httpClient.post('/users/register', user, {
			observe: "body"
		});
	}

	loginUser(user) {
		return this.httpClient.post('/auth/login', user);
	}

	forgotPassword(forgot) {
		return this.httpClient.post('/users/forgotPassword', forgot, {
			observe: "body"
		});
	}

	becomeUser(data) {
		return this.httpClient.post('/users/becomeUser', data, {
			observe: "body",
			responseType:"json"
		});
	}

	logoutUser():Observable<boolean> {
		return this.httpClient.post('/auth/logout',{}).pipe(
			map(res => {
				return true;
			}))
	}
	enableFullAccessMode() {
		return this.httpClient.post('/users/enableFullAccessMode', {}, {
            observe: "body",
            responseType: "text"
		});
	}

	/**
	 * Checks if the standard login button should be disabled
	 * This happens when a user tries to login from a different IP address
	 * and they were logged in recently but don't have any active sessions
	 */
	checkLoginStatus() {
		return this.httpClient.post('/auth/checkLoginStatus', {}, {
			observe: "body",
			responseType: "json"
		});
	}

	/**
	 * Initiates the MSAL login flow
	 * This is used for the SSO Login button
	 */
	loginWithMsal(): Observable<any> {
		// Define the scopes for the login request
		const loginRequest = {
			scopes: ['user.read']
		};

		// Attempt to login with a popup
		return new Observable(observer => {
			// Check if an interaction is already in progress (both local and MSAL states)
			const msalInteractionInProgress = this.currentMsalInteractionStatus !== InteractionStatus.None;
			if (this.interactionInProgress || msalInteractionInProgress) {
				console.warn('MSAL interaction is already in progress. Attempting to clear stale interaction status before proceeding.', {
					localInteractionFlag: this.interactionInProgress,
					msalInteractionStatus: this.currentMsalInteractionStatus,
					msalInteractionInProgress: msalInteractionInProgress,
					activeAccount: !!this.msalService.instance.getActiveAccount(),
					timestamp: new Date().toISOString()
				});

				// Use enhanced interaction clearing with retry logic
				this.clearInteractionWithRetry(loginRequest, observer);

				return;
			}

			// REMOVED: Popup capability test - it was causing race conditions with MSAL
			// MSAL library handles popup blockers gracefully by catching errors
			// and the error handler will offer redirect fallback if popup fails
			console.log('Proceeding with MSAL popup authentication');

			// Proceed with the login attempt
			this.proceedWithMsalLogin(loginRequest, observer);
		});
	}

	/**
	 * Proceeds with the MSAL login flow
	 * This is a helper method used by loginWithMsal
	 * @param loginRequest The login request configuration
	 * @param observer The observer to emit events to
	 */
	private proceedWithMsalLogin(loginRequest: any, observer: any): void {
		try {
			// Set interaction in progress before starting
			this.interactionInProgress = true;

			console.log('[MSAL Popup] Attempting to open login popup', {
				timestamp: new Date().toISOString(),
				scopes: loginRequest.scopes
			});

			// Use loginPopup to show the Microsoft login popup
			// Note: We're not clearing the active account anymore as it might cause the popup to close prematurely
			this.msalService.loginPopup(loginRequest)
				.subscribe((response: AuthenticationResult) => {

					console.log('[MSAL Popup] Popup authentication successful', {
						accountUsername: response.account?.username,
						timestamp: new Date().toISOString()
					});

					// Make backend call to verify account exists and create session
					this.httpClient.post(window.location.origin + '/msalAuth/login', {
						accessToken: response.accessToken,
						account: response.account
					}).subscribe(
						(res: any) => {
							// Clear interaction state on successful backend response
							this.interactionInProgress = false;

							// Check if account verification failed
							if (res && res.code === -3) {
								// Account not found - return the error response
								observer.next(res);
								observer.complete();
								return;
							}

							// Account verified successfully - return success response
							const successResponse = {
								obj: {
									code: 1,
									message: 'MSAL login successful'
								},
								accessToken: response.accessToken,
								account: response.account,
								scopes: response.scopes,
								correlationId: response.correlationId
							};

							observer.next(successResponse);
							observer.complete();
						},
						error => {
							console.error('Backend login call failed:', error);

							// Clear interaction state even if backend fails
							this.interactionInProgress = false;

							// If backend call fails, still return the MSAL success but log the error
							const successResponse = {
								obj: {
									code: 1,
									message: 'MSAL login successful (backend verification failed)'
								},
								accessToken: response.accessToken,
								account: response.account,
								scopes: response.scopes,
								correlationId: response.correlationId
							};

							observer.next(successResponse);
							observer.complete();
						}
					);
				},
				error => {
					// Handle MSAL login error
					console.error('[MSAL Popup] loginPopup failed', {
						errorCode: error?.errorCode,
						errorMessage: error?.message,
						timestamp: new Date().toISOString()
					});

					// Check if this is a popup-related error and offer redirect fallback
					if (this.isPopupRelatedError(error)) {
						console.warn('[MSAL Popup] Popup error detected, offering redirect fallback', {
							errorType: error?.name,
							errorCode: error?.errorCode
						});

						// Notify user about the popup issue and offer redirect option
						const fallbackMessage = {
							type: 'popup_error_fallback',
							message: 'Popup authentication failed. Would you like to try redirect-based authentication?',
							error: error,
							canRetryWithRedirect: true,
							userAction: 'Click "Try Redirect" to use redirect-based authentication instead.'
						};

						// Clear the interaction status
						this.interactionInProgress = false;
						this.clearInteractionStatus();

						observer.next(fallbackMessage);
						observer.complete();
						return;
					}

					this.logSsoLoginError(error);

					// Clear the interaction status on error
					// This is important for the button text to update correctly
					this.interactionInProgress = false;

					// Clear the interaction status in Redis
					// This ensures the error state doesn't persist across page refreshes
					this.clearInteractionStatus();

					observer.error(error);
				});
		} catch (error) {
			// Handle any unexpected errors
			this.logSsoLoginError(error);

			// Clear the interaction status on error
			// This is important for the button text to update correctly
			this.interactionInProgress = false;

			// Clear the interaction status in Redis
			this.clearInteractionStatus();

			observer.error(error);
		}
	}

	/**
	 * Cleanup method to unsubscribe from observables
	 * Call this method when the component is destroyed
	 */
	cleanup(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

 /**
	 * Manually clears the interaction status
	 * This can be useful for debugging or for cases where we need to force the interaction status to be reset
	 */
	public clearInteractionStatus(): void {
		// Clear the active account in MSAL to help clear any internal interaction state
		try {
			// This helps clear the MSAL library's internal interaction state
			this.msalService.instance.setActiveAccount(null);
		} catch (error) {
			console.error('Failed to clear active account in MSAL:', error);
		}

		// Clear the interaction status in Redis
		this.httpClient.delete(`/msalAuth/interactionStatus/clear/${this.userId}`)
			.subscribe(
				(response: any) => {
					// Reset the interactionInProgress property
					this.interactionInProgress = false;
				},
				(error) => {
					console.error('Failed to clear interaction status from Redis:', error);

					// Fall back to localStorage if Redis clear fails
					this.clearInteractionStatusFromLocalStorage();
				}
			);
	}

	/**
	 * Fallback method to clear interaction status from localStorage
	 * This is used if the Redis clear fails
	 */
	private clearInteractionStatusFromLocalStorage(): void {
		// Clear the active account in MSAL to help clear any internal interaction state
		try {
			// This helps clear the MSAL library's internal interaction state
			this.msalService.instance.setActiveAccount(null);
		} catch (error) {
			console.error('Failed to clear active account in MSAL (localStorage fallback):', error);
		}

		// Clear the interaction status in localStorage
		localStorage.removeItem('msal.interaction.status');
		localStorage.removeItem('msal.interaction.timestamp');
		localStorage.removeItem('msal.interaction.request');

		// Reset the interactionInProgress property
		this.interactionInProgress = false;
	}

	/**
	 * Force clear all interaction state (nuclear option)
	 * This clears both local state and attempts to clear MSAL's internal state
	 */
	private forceClearAllInteractionState(): void {
		// Clear local state immediately
		this.interactionInProgress = false;

		// Clear MSAL's active account
		try {
			this.msalService.instance.setActiveAccount(null);
		} catch (error) {
			console.error('Failed to clear MSAL active account:', error);
		}

		// Clear localStorage state
		try {
			localStorage.removeItem('msal.interaction.status');
			localStorage.removeItem('msal.interaction.timestamp');
			localStorage.removeItem('msal.interaction.request');
			localStorage.removeItem('msal.interaction.expiration');
		} catch (error) {
			console.error('Failed to clear localStorage MSAL state:', error);
		}

		// Clear Redis state (async, but don't wait for it)
		this.httpClient.delete(`/msalAuth/interactionStatus/clear/${this.userId}`)
			.subscribe(
				(response: any) => {
				},
				(error) => {
					console.error('Failed to clear Redis interaction status:', error);
				}
			);
	}

 /**
	 * Checks if an MSAL login interaction is currently in progress
	 * @returns {boolean} True if a login interaction is in progress, false otherwise
	 */
	isInteractionInProgress(): boolean {
		// Return the current value of the interactionInProgress property
		// This property is updated by the checkAndResetInteractionStatus method
		// and by the subscription to the inProgress$ observable
		// It will only be true if a login interaction is in progress
		return this.interactionInProgress;
	}

	/**
	 * Checks if an MSAL login interaction is currently in progress (asynchronous version)
	 * This method returns a promise that resolves to a boolean
	 * @returns {Promise<boolean>} Promise that resolves to true if a login interaction is in progress, false otherwise
	 */
	async isInteractionInProgressAsync(): Promise<boolean> {
		try {
			// Check if there's an interaction status in Redis
			const response: any = await this.httpClient.get(`/msalAuth/interactionStatus/get/${this.userId}`).toPromise();

			// Log the current interaction status from Redis for debugging
			console.log('MSAL interaction status from Redis (async):', response);

			// If the interaction status is not stale and not 'none', check if it's a login interaction
			if (response && response.status && response.status.toLowerCase() !== 'none' && !response.stale) {
				// Only return true if the status is from a login attempt
				// This ensures that the "SSO Login in progress..." message only appears after a login has been attempted
				if (response.status.toLowerCase() === 'login') {
					console.log('Login interaction is in progress (async)');
					return true;
				} else {
					console.log('Interaction is in progress, but it\'s not a login interaction (async)');
					return false;
				}
			}

			return false;
		} catch (error) {
			console.error('Failed to check interaction status from Redis (async):', error);

			// Fall back to localStorage if Redis check fails
			return this.isInteractionInProgressFromLocalStorage();
		}
	}

	/**
	 * Fallback method to check if an MSAL login interaction is currently in progress from localStorage
	 * This is used if the Redis check fails
	 * @returns {boolean} True if a login interaction is in progress, false otherwise
	 */
	private isInteractionInProgressFromLocalStorage(): boolean {
		// Get the current value from localStorage
		const inProgress = localStorage.getItem('msal.interaction.status');

		// Log the current interaction status from localStorage for debugging
		console.log('MSAL interaction status from localStorage (fallback):', {
			inProgress
		});

		// If there's a value in localStorage, check if it's a login interaction
		if (inProgress) {
			// Only return true if the status is from a login attempt
			// This ensures that the "SSO Login in progress..." message only appears after a login has been attempted
			if (inProgress.toLowerCase() === 'login') {
				console.log('Login interaction is in progress (localStorage)');
				return true;
			} else {
				console.log('Interaction is in progress, but it\'s not a login interaction (localStorage)');
				return false;
			}
		}

		// Otherwise, fall back to the tracked property
		return this.interactionInProgress;
	}

	/**
	 * Returns an observable that emits the current interaction status
	 * @returns {Observable<boolean>} An observable that emits true when a login interaction starts and false when it ends
	 */
	getInteractionStatus(): Observable<boolean> {
		// First check the current status
		const currentStatus = this.isInteractionInProgress();

		// Then subscribe to future changes
		return new Observable<boolean>(observer => {
			// Emit the current status immediately
			observer.next(currentStatus);

			// Subscribe to the inProgress$ observable for future updates
			const subscription = this.msalBroadcastService.inProgress$.pipe(
				map((status: InteractionStatus) => {
					// Only consider login interactions as "in progress"
					// This ensures that the "SSO Login in progress..." message only appears after a login has been attempted
					const inProgress = status === InteractionStatus.Login;

					// If the status changes, store it in Redis
					if (status !== InteractionStatus.None) {
						this.storeInteractionStatus(status);
					}

					// Log the interaction status change for debugging
					console.log('MSAL interaction status changed in observable:', {
						status,
						inProgress,
						isLogin: status === InteractionStatus.Login
					});

					return inProgress;
				})
			).subscribe(observer);

			// Return a cleanup function
			return () => {
				subscription.unsubscribe();
			};
		});
	}

	/**
	 * Polls the interaction status from Redis at regular intervals
	 * This can be used to keep the interaction status in sync across tabs
	 * @param intervalMs The interval in milliseconds between polls (default: 5000)
	 * @returns {Observable<boolean>} An observable that emits the interaction status at regular intervals
	 */
	pollInteractionStatus(intervalMs: number = 5000): Observable<boolean> {
		return new Observable<boolean>(observer => {
			// Check the interaction status immediately
			this.isInteractionInProgressAsync().then(inProgress => {
				observer.next(inProgress);
			});

			// Set up a polling interval
			const intervalId = setInterval(() => {
				this.isInteractionInProgressAsync().then(inProgress => {
					observer.next(inProgress);
				});
			}, intervalMs);

			// Return a cleanup function
			return () => {
				clearInterval(intervalId);
			};
		});
	}

	/**
	 * Enhanced interaction clearing with retry logic
	 * ENHANCED: Reduced retry attempts and faster fallback to redirect authentication
	 */
	private clearInteractionWithRetry(loginRequest: any, observer: any, attempt: number = 1, maxAttempts: number = 1): void {
		// Force clear all interaction state
		this.forceClearAllInteractionState();

		// ENHANCED: Reduced wait time for faster response
		const waitTime = 1000; // Fixed 1 second wait instead of progressive delays

		setTimeout(() => {
			// Check if the interaction status is still in progress
			const stillInProgress = this.interactionInProgress || this.currentMsalInteractionStatus !== InteractionStatus.None;

			if (!stillInProgress) {
				this.proceedWithMsalLogin(loginRequest, observer);
			} else {
				// ENHANCED: Immediately fall back to redirect authentication instead of showing popup
				console.warn(`Interaction still in progress after attempt ${attempt}. Automatically switching to redirect authentication.`);

				// Automatically use redirect authentication instead of showing the stuck dialog

				try {
					// Store a flag in localStorage to indicate we're using redirect authentication
					localStorage.setItem('msal.using.redirect.auth', 'true');
					localStorage.setItem('msal.redirect.timestamp', Date.now().toString());
					localStorage.setItem('msal.redirect.reason', 'interaction_stuck_auto_fallback');

					// Use loginRedirect instead of loginPopup
					this.msalService.loginRedirect(loginRequest);

					// Notify the observer that we're switching to redirect
					const redirectMessage = {
						type: 'auto_redirect_fallback',
						message: 'Switching to redirect-based authentication...',
						reason: 'interaction_stuck',
						userAction: 'Page will redirect to Microsoft login'
					};
					observer.next(redirectMessage);
					observer.complete();

				} catch (redirectError) {
					console.error('Auto-redirect failed, showing user options as fallback');

					// Only show the popup if redirect also fails
					const interactionError = {
						type: 'interaction_stuck',
						message: 'Authentication is stuck in progress. Would you like to try a different approach?',
						error: {
							name: 'BrowserAuthError',
							message: 'interaction_in_progress: Interaction is currently in progress. Please ensure that this interaction has been completed before calling an interactive API.',
							errorCode: 'interaction_in_progress'
						},
						canRetryWithRedirect: true,
						canForceReset: true,
						userAction: 'Choose "Force Reset" to clear all authentication state, or "Try Redirect" to use redirect-based authentication.'
					};

					this.logSsoLoginError(interactionError.error);
					observer.next(interactionError);
					observer.complete();
				}
			}
		}, waitTime);
	}

	// REMOVED: testPopupCapability() function
	// This function was causing race conditions with MSAL popup authentication
	// MSAL library handles popup blockers gracefully through its error handling

	/**
	 * Checks if an error is related to popup functionality or interaction issues
	 * @param error The error to check
	 * @returns true if the error is popup-related or interaction-related, false otherwise
	 */
	private isPopupRelatedError(error: any): boolean {
		if (!error) return false;

		const errorMessage = error.message || '';
		const errorCode = error.errorCode || '';
		const errorName = error.name || '';

		// Check for common popup-related error patterns
		const popupErrorPatterns = [
			'popup_window_error',
			'empty_window_error',
			'window.open returned null',
			'popup_window_error: Error opening popup window',
			'popup blocker',
			'popup blocked',
			'BrowserAuthError: popup_window_error',
			'BrowserAuthError: empty_window_error',
			'interaction_in_progress',
			'interaction is currently in progress'
		];

		return popupErrorPatterns.some(pattern =>
			errorMessage.toLowerCase().includes(pattern.toLowerCase()) ||
			errorCode.toLowerCase().includes(pattern.toLowerCase()) ||
			errorName.toLowerCase().includes(pattern.toLowerCase())
		);
	}

	/**
	 * Detects if a popup was blocked by the browser
	 * This is a heuristic check since browsers don't always report popup blocking clearly
	 * @returns true if popup appears to be blocked, false otherwise
	 */
	private detectPopupBlocked(): boolean {
		try {
			// Try to open a test window
			const testWindow = window.open('', '', 'width=1,height=1');
			if (testWindow === null) {
				console.warn('[Popup Blocker] Detected: window.open returned null');
				return true;
			}
			// Close the test window if it opened
			testWindow.close();
			return false;
		} catch (e) {
			console.warn('[Popup Blocker] Detected: Exception thrown when trying to open window', e);
			return true;
		}
	}

	/**
	 * Initiates redirect-based authentication as a fallback
	 * @param loginRequest The login request parameters
	 */
	public loginWithRedirectFallback(loginRequest?: any): void {
		try {
			// Store a flag in localStorage to indicate we're using redirect authentication
			localStorage.setItem('msal.using.redirect.auth', 'true');
			localStorage.setItem('msal.redirect.timestamp', Date.now().toString());
			localStorage.setItem('msal.redirect.reason', 'popup_fallback');

			// Use loginRedirect instead of loginPopup
			this.msalService.loginRedirect(loginRequest || {
				scopes: ['openid', 'profile', 'email']
			});

		} catch (redirectError) {
			console.error('Failed to use redirect authentication:', redirectError);
			throw new Error('Failed to authenticate. Please try again or contact support.');
		}
	}

	/**
	 * Force resets all MSAL authentication state (nuclear option)
	 * This clears everything and allows the user to start fresh
	 */
	public forceResetAuthentication(): void {
		try {
			// Clear active account first
			this.msalService.instance.setActiveAccount(null);

			// Clear all MSAL accounts using logout
			const accounts = this.msalService.instance.getAllAccounts();
			if (accounts.length > 0) {
				// Use logoutPopup to clear accounts (this is the proper way to clear MSAL state)
				try {
					this.msalService.logoutPopup({
						account: accounts[0],
						mainWindowRedirectUri: window.location.origin
					}).subscribe({
						next: () => console.log('MSAL accounts cleared via logout'),
						error: (error) => console.warn('MSAL logout failed, continuing with manual cleanup:', error)
					});
				} catch (logoutError) {
					console.warn('MSAL logout popup failed, continuing with manual cleanup:', logoutError);
				}
			}

			// Clear all localStorage MSAL data
			Object.keys(localStorage).forEach(key => {
				if (key.startsWith('msal.') || key.includes('msal')) {
					localStorage.removeItem(key);
				}
			});

			// Clear our local state
			this.interactionInProgress = false;
			this.userId = null;

			// Clear Redis state
			this.httpClient.delete(`/msalAuth/interactionStatus/clear/${this.userId}`)
				.subscribe(
					() => console.log('Cleared Redis state during force reset'),
					(error) => console.warn('Failed to clear Redis state during force reset:', error)
				);

		} catch (error) {
			console.error('Error during force reset:', error);
			throw new Error('Failed to reset authentication state. Please refresh the page and try again.');
		}
	}

	/**
	 * Logs an SSO login error to the server
	 * @param error The error that occurred
	 * @param email The email of the user trying to log in (if available)
	 */
	logSsoLoginError(error: any, email?: string): void {
		// Enhanced browser environment information
		const browserInfo = {
			userAgent: navigator.userAgent,
			platform: navigator.platform,
			vendor: navigator.vendor,
			hasCrypto: typeof window.crypto !== 'undefined',
			hasSubtle: typeof window.crypto !== 'undefined' && typeof window.crypto.subtle !== 'undefined',
			hasDigest: typeof window.crypto !== 'undefined' &&
				typeof window.crypto.subtle !== 'undefined' &&
				typeof window.crypto.subtle.digest !== 'undefined',
			secureContext: window.isSecureContext,
			protocol: window.location.protocol,
			host: window.location.host,
			origin: window.location.origin,
			href: window.location.href,
			cookiesEnabled: navigator.cookieEnabled,
			onLine: navigator.onLine,
			language: navigator.language,
			languages: navigator.languages,
			doNotTrack: navigator.doNotTrack,
			hardwareConcurrency: navigator.hardwareConcurrency,
			maxTouchPoints: navigator.maxTouchPoints,
			webdriver: (navigator as any).webdriver,
			windowSize: {
				innerWidth: window.innerWidth,
				innerHeight: window.innerHeight,
				outerWidth: window.outerWidth,
				outerHeight: window.outerHeight,
				screenWidth: window.screen.width,
				screenHeight: window.screen.height
			}
		};

 	// Extract detailed error information with MSAL-specific context
 	const errorDetails: {
 		message: any;
 		name: any;
 		stack: any;
 		code: any;
 		errorCode: any;
 		errorDesc: any;
 		subError: any;
 		correlationId: any;
 		isPkceError: any;
 		isCryptoError: any;
 		isInteractionError: any;
 		fullError?: string;
 	} = {
 		message: error?.message || 'No error message',
 		name: error?.name || 'No error name',
 		stack: error?.stack || 'No stack trace',
 		code: error?.code || 'No error code',
 		errorCode: error?.errorCode || 'No error code',
 		errorDesc: error?.errorDesc || 'No error description',
 		subError: error?.subError || 'No sub error',
 		correlationId: error?.correlationId || 'No correlation ID',
 		isPkceError: error?.message?.includes('pkce_not_created') || false,
 		isCryptoError: error?.message?.includes('crypto') || error?.message?.includes('subtle') || false,
 		isInteractionError: error?.message?.includes('interaction_in_progress') || error?.errorCode === 'interaction_in_progress' || false
 	};

 	// Get current MSAL state for debugging
 	const msalState = {
 		interactionStatus: this.currentMsalInteractionStatus,
 		activeAccount: this.msalService.instance.getActiveAccount(),
 		allAccounts: this.msalService.instance.getAllAccounts(),
 		configuration: {
 			clientId: this.msalService.instance.getConfiguration()?.auth?.clientId,
 			authority: this.msalService.instance.getConfiguration()?.auth?.authority
 		}
 	};

 	// Get local interaction state
 	const localInteractionState = {
 		interactionInProgress: this.interactionInProgress,
 		localStorage: {
 			status: localStorage.getItem('msal.interaction.status'),
 			timestamp: localStorage.getItem('msal.interaction.timestamp'),
 			expiration: localStorage.getItem('msal.interaction.expiration')
 		},
 		userId: this.userId,
 		sessionId: this.sessionId
 	};

		// Safely stringify the error object, handling circular references
		try {
			// Create a safe copy of the error object with only primitive properties
			const safeErrorObj = {};
			if (typeof error === 'object' && error !== null) {
				Object.getOwnPropertyNames(error).forEach(key => {
					const value = error[key];
					if (typeof value !== 'object' || value === null) {
						safeErrorObj[key] = value;
					} else if (typeof value === 'function') {
						safeErrorObj[key] = '[Function]';
					} else {
						try {
							// Try to stringify the value, but catch any errors
							safeErrorObj[key] = JSON.stringify(value);
						} catch (e) {
							safeErrorObj[key] = '[Complex Object]';
						}
					}
				});
				errorDetails.fullError = JSON.stringify(safeErrorObj);
			} else {
				errorDetails.fullError = String(error);
			}
		} catch (e) {
			// If all else fails, just use a simple string representation
			errorDetails.fullError = 'Error object could not be stringified: ' + String(error);
		}

		// Combine all information for comprehensive logging
		const detailedError = JSON.stringify({
			error: errorDetails,
			browser: browserInfo,
			msalState: msalState,
			localState: localInteractionState,
			timestamp: new Date().toISOString(),
			debugInfo: {
				isInteractionInProgress: errorDetails.isInteractionError,
				hasActiveAccount: !!msalState.activeAccount,
				accountCount: msalState.allAccounts?.length || 0,
				localStorageHasState: !!(localInteractionState.localStorage.status || localInteractionState.localStorage.timestamp)
			}
		});

		// Create a more informative error summary, using "Unknown error" as a fallback
		const errorSummary = (errorDetails.name !== 'No error name' || errorDetails.message !== 'No error message')
			? `${errorDetails.name}: ${errorDetails.message}`
			: 'Unknown error';

		console.log('Attempting to log SSO error to server:', {
			endpoint: '/msalAuth/loginError',
			errorSummary,
			isPkceError: errorDetails.isPkceError,
			isCryptoError: errorDetails.isCryptoError
		});

		// Log the error to the server with enhanced details
		// Use a timeout to ensure the request is sent even if the page is about to unload
		const xhr = new XMLHttpRequest();
 		xhr.open('POST', window.location.origin + '/msalAuth/loginError', true);
		xhr.setRequestHeader('Content-Type', 'application/json');
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				console.log('SSO login error logged successfully');
				console.log('Detailed error information:', {
					errorDetails,
					browserInfo
				});
			} else {
				console.error('Failed to log SSO login error. Status:', xhr.status, xhr.statusText);
				console.error('Response:', xhr.responseText);
			}
		};
		xhr.onerror = (e) => {
			console.error('Network error when logging SSO error:', e);
			console.error('You may need to check CORS configuration if this is a cross-origin request');
		};
		xhr.send(JSON.stringify({
			error: detailedError,
			email: email || 'unknown'
		}));

		// Also try with HttpClient as a backup
		this.httpClient.post(window.location.origin + '/msalAuth/loginError', {
			error: detailedError,
			email: email || 'unknown'
		}).subscribe(
			() => {
				console.log('SSO login error logged successfully via HttpClient');
			},
			err => {
				console.error('Failed to log SSO login error via HttpClient:', err);
				// Log more details about the error
				console.error('Error details:', {
					status: err.status,
					statusText: err.statusText,
					message: err.message,
					url: err.url,
					error: err.error
				});
			}
		);
	}

	/**
	 * Debug method to get comprehensive MSAL state information
	 * This can be called from the browser console for troubleshooting
	 */
	public debugMsalState(): any {
		const debugInfo = {
			timestamp: new Date().toISOString(),
			localState: {
				interactionInProgress: this.interactionInProgress,
				userId: this.userId,
				sessionId: this.sessionId
			},
			msalState: {
				interactionStatus: this.currentMsalInteractionStatus,
				activeAccount: this.msalService.instance.getActiveAccount(),
				allAccounts: this.msalService.instance.getAllAccounts(),
				configuration: this.msalService.instance.getConfiguration()
			},
			localStorage: {
				status: localStorage.getItem('msal.interaction.status'),
				timestamp: localStorage.getItem('msal.interaction.timestamp'),
				expiration: localStorage.getItem('msal.interaction.expiration')
			},
			browserInfo: {
				userAgent: navigator.userAgent,
				cookiesEnabled: navigator.cookieEnabled,
				onLine: navigator.onLine,
				hasCrypto: typeof window.crypto !== 'undefined',
				hasSubtle: typeof window.crypto !== 'undefined' && typeof window.crypto.subtle !== 'undefined',
				secureContext: window.isSecureContext,
				protocol: window.location.protocol,
				origin: window.location.origin
			}
		};

		return debugInfo;
	}

	/**
	 * Debug method to force clear all MSAL state
	 * This can be called from the browser console for troubleshooting
	 */
	public debugForceClearAll(): void {
		// Clear local flags
		this.interactionInProgress = false;

		// Clear MSAL instance state
		try {
			this.msalService.instance.setActiveAccount(null);
			// Note: removeAccount() method doesn't exist on IPublicClientApplication
			// The accounts will be cleared when the user logs out properly
			console.log('Cleared active account from MSAL instance');
		} catch (error) {
			console.error('Error clearing MSAL instance:', error);
		}

		// Clear localStorage
		localStorage.removeItem('msal.interaction.status');
		localStorage.removeItem('msal.interaction.timestamp');
		localStorage.removeItem('msal.interaction.expiration');
		localStorage.removeItem('msal.interaction.request');

		// Clear Redis state
		this.httpClient.delete(`/msalAuth/interactionStatus/clear/${this.userId}`)
			.subscribe(
				(response) => console.log('Cleared Redis state:', response),
				(error) => console.error('Failed to clear Redis state:', error)
			);
	}

	/**
	 * Store SSO registration data for first-time users
	 * This data comes from the MSAL login error response when user doesn't exist
	 *
	 * @param ssoData - The SSO data containing firstname, lastname, email, domain, isSsoUser
	 */
	setSsoRegistrationData(ssoData: any): void {
		this.ssoRegistrationData = ssoData;
	}

	/**
	 * Retrieve SSO registration data
	 * Returns the stored SSO data and clears it after retrieval
	 *
	 * @returns The SSO registration data or null if not available
	 */
	getSsoRegistrationData(): any {
		const data = this.ssoRegistrationData;
		// Clear after retrieval to prevent reuse
		this.ssoRegistrationData = null;
		return data;
	}

	/**
	 * Clear SSO registration data
	 * Used when user cancels registration or completes it
	 */
	clearSsoRegistrationData(): void {
		this.ssoRegistrationData = null;
	}
}
